import { Decimal } from 'decimal.js'

import {
  resolveDenomination,
  resolveEffectiveAmount,
} from '@/features/order/order-form/lib/instrument-resolution'
import { baseCurrencyHint } from '@/features/order/order-form/lib/submitters/shared'
import type { OrderFormT } from '@/features/order/order-form/lib/submitters/types'
import type { UnifiedItemValues } from '@/features/order/order-form/lib/unified-order-schema'
import {
  planScaleLadder,
  SCALE_MAX_ORDERS,
  SCALE_MIN_ORDERS,
  type ScaleLadderError,
  type ScaleLadderResult,
  scaleLadderSpec,
  type ScaleSide,
} from '@/features/scale/lib/scale-ladder'
import { ScaleAlgoParams } from '@/types/generated/algo'
import { Denomination, OrderSide } from '@/types/generated/common/trading'
import { Exchange, Instrument, TradeVenue } from '@/types/generated/common/types'

/** The order form's scale fields on a fresh form and after each reset. */
export const SCALE_FORM_DEFAULTS = {
  scaleStartPrice: '',
  scaleEndPrice: '',
  scaleOrderCount: '5',
  scaleSizeRatio: '',
}

export type ScaleFormFields = Partial<typeof SCALE_FORM_DEFAULTS>

const clean = (value: string | undefined) => (value ?? '').replace(/,/g, '').trim()

/** Parses the level-count input; NaN when it is not a whole number. */
export function parseScaleOrderCount(value: string | undefined): number {
  const trimmed = clean(value)
  return /^\d+$/.test(trimmed) ? Number(trimmed) : NaN
}

/** One run exactly as it will be sent, with the ladder the server will plan from it. */
export type ScaleRun = {
  denomination: Denomination
  quantity: string
  orderCount: number
  startPrice: string
  endPrice: string
  /** Blank means a flat ladder; the server reads absence as 1. */
  sizeRatio: string
  /** Null until the instrument is known — the server re-plans and decides either way. */
  plan: ScaleLadderResult | null
}

/**
 * The single source for a Scale run: the preview, the submit button and the submitter
 * all read this, so what is checked is what is sent. Options are base-sized in
 * contracts × contractSize, as for TWAP and Limit Chase; the multiply is in Decimal so
 * the quantity never goes out as a float artefact or in exponent notation.
 */
export function planScaleRun(
  instrumentMap: Map<string, Instrument>,
  item: Pick<UnifiedItemValues, 'symbol' | 'side' | 'baseCurrency' | 'amountUnit' | 'itemType'>,
  amount: number,
  fields: ScaleFormFields
): ScaleRun {
  const denomination = resolveDenomination(
    instrumentMap,
    item.symbol,
    item.itemType,
    item.amountUnit,
    baseCurrencyHint(item.baseCurrency)
  )
  const quoteDenominated = denomination === Denomination.DENOMINATION_QUOTE
  const contractSize = resolveEffectiveAmount(instrumentMap, item.symbol, item.itemType, 1)
  const run = {
    denomination,
    quantity: new Decimal(amount).times(contractSize).toFixed(),
    orderCount: parseScaleOrderCount(fields.scaleOrderCount),
    startPrice: clean(fields.scaleStartPrice),
    endPrice: clean(fields.scaleEndPrice),
    sizeRatio: clean(fields.scaleSizeRatio),
  }
  const instrument = instrumentMap.get(item.symbol)
  const plan = instrument
    ? planScaleLadder(
        { side: item.side, quoteDenominated, ...run },
        scaleLadderSpec(instrument, quoteDenominated)
      )
    : null
  return { ...run, plan }
}

/**
 * ScaleAlgoParams for a planned run. exchange/tradeVenue stay UNSPECIFIED and accountId
 * is re-stamped server-side from the authorized account, as for every other algo.
 */
export function buildScaleAlgoParams(args: {
  symbol: string
  side: ScaleSide
  accountId: string
  run: ScaleRun
  reduceOnly: boolean
  postOnly: boolean
}): ScaleAlgoParams {
  const { symbol, side, accountId, run, reduceOnly, postOnly } = args
  return {
    accountId,
    symbol,
    orderSide: side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
    quantity: run.quantity,
    denomination: run.denomination,
    exchange: Exchange.EXCHANGE_UNSPECIFIED,
    tradeVenue: TradeVenue.TRADE_VENUE_UNSPECIFIED,
    orderCount: run.orderCount,
    startPrice: { absolute: run.startPrice },
    endPrice: { absolute: run.endPrice },
    sizeRatio: run.sizeRatio || undefined,
    reduceOnly: reduceOnly || undefined,
    postOnly: postOnly || undefined,
  }
}

function invalidFieldMessage(
  field: Extract<ScaleLadderError, { kind: 'invalid' }>['field'],
  t: OrderFormT
): string {
  switch (field) {
    case 'quantity':
      return t('enterValidAmount')
    case 'orderCount':
      return t('scaleForm.errors.orderCount', { min: SCALE_MIN_ORDERS, max: SCALE_MAX_ORDERS })
    case 'startPrice':
      return t('scaleForm.errors.startPrice')
    case 'endPrice':
      return t('scaleForm.errors.endPrice')
    case 'sizeRatio':
      return t('scaleForm.errors.sizeRatio')
  }
}

/** User-facing text for a ladder the preview refuses. Level numbers are 1-based. */
export function scaleLadderErrorMessage(error: ScaleLadderError, t: OrderFormT): string {
  const fmt = (d: Decimal) => d.toString()
  switch (error.kind) {
    case 'invalid':
      return invalidFieldMessage(error.field, t)
    case 'equalBounds':
      return t('scaleForm.errors.equalBounds')
    case 'noTick':
      return t('scaleForm.errors.noTick')
    case 'nonPositiveLevel':
      return t('scaleForm.errors.nonPositiveLevel', { level: error.index + 1 })
    case 'duplicateLevel':
      return t('scaleForm.errors.duplicateLevel', { price: fmt(error.price) })
    case 'belowMinimum':
      return t('scaleForm.errors.belowMinimum', {
        level: error.index + 1,
        quantity: fmt(error.quantity),
        minimum: fmt(error.minimum),
      })
  }
}
